/**
 * Shared Open Graph / Twitter Card metadata for Maxiom Apps marketing pages.
 *
 * Social crawlers (LinkedIn, Slack, facebookexternalhit) do not run JavaScript,
 * so react-helmet tags never reach them. `Seo` keeps client navigations in sync,
 * and `frontend/scripts/prerender-open-graph.js` writes the same tags into each
 * route's initial HTML at build time.
 *
 * Cover art: pass `image` / registry `ogImage` or `coverImage` (root-relative
 * path or absolute https URL, 1200×630). Otherwise the brand default is used.
 */

export const SITE_ORIGIN = "https://maxiomapps.com";
export const SITE_NAME = "Maxiom Apps";
export const DEFAULT_OG_IMAGE_PATH = "/og/maxiom-apps-og.png";
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;

export const STATIC_PAGES = {
  home: {
    path: "/",
    title: "Maxiom Apps | Custom Mobile & Web App Development",
    description:
      "We build iOS, Android, web, and AI apps from our DC Metro studio. Since 2002. $100M+ contract value. 221+ clients. Schedule a call.",
    keywords:
      "app development, mobile app development, web app development, custom software, iOS, Android, React, Angular, Vue, AI, machine learning, e-commerce",
  },
  contact: {
    path: "/contact",
    title: "Contact Us | Maxiom Apps - Schedule a Free App Development Consultation",
    description:
      "Contact Maxiom Apps for consultations or quotes. hello@maxiomtech.com • 703-942-9420. We reply within one business day.",
  },
  caseStudies: {
    path: "/case-studies",
    title: "Case Studies & Portfolio | Maxiom Apps - App Development Success Stories",
    description:
      "Case studies spanning AT&T, Microsoft, BMW, and more, see how Maxiom Apps ships mobile, web, and custom software.",
  },
  process: {
    path: "/process",
    title: "Our App Development Process | Maxiom Apps - Agile Methodology",
    description:
      "Six-phase delivery: discovery, UX, agile build, QA, launch, iterate, with demos each sprint so stakeholders stay aligned.",
  },
  blog: {
    path: "/blog",
    title: "Blog | Maxiom Apps",
    description:
      "Practical notes for founders and product leads scoping a first mobile or web app with a vendor.",
  },
};

function oneLine(value) {
  return String(value == null ? "" : value).replace(/\s+/g, " ").trim();
}

export function withBrand(title) {
  const text = oneLine(title);
  if (!text) {
    throw new Error("Open Graph title is empty");
  }
  if (/maxiom apps/i.test(text)) return text;
  return `${text} | ${SITE_NAME}`;
}

export function pageUrl(pathname) {
  const path = pathname || "/";
  if (path === "/") return SITE_ORIGIN;
  if (!path.startsWith("/")) {
    throw new Error(`Open Graph path must start with /: ${path}`);
  }
  return `${SITE_ORIGIN}${path.replace(/\/+$/, "")}`;
}

export function absoluteOgImage(image) {
  if (!image) return `${SITE_ORIGIN}${DEFAULT_OG_IMAGE_PATH}`;
  const value = oneLine(image);
  if (value.startsWith("https://")) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `${SITE_ORIGIN}${value}`;
  throw new Error(`og:image must be an https URL or a site path: ${value}`);
}

function imageMime(url) {
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  if (clean.endsWith(".jpg") || clean.endsWith(".jpeg")) return "image/jpeg";
  if (clean.endsWith(".webp")) return "image/webp";
  if (clean.endsWith(".gif")) return "image/gif";
  return "image/png";
}

export function buildSocialMeta(input) {
  const title = withBrand(input.title);
  const description = oneLine(input.description);
  if (!description) {
    throw new Error(`Open Graph description is empty for ${input.path || title}`);
  }
  const ogDescription = oneLine(input.ogDescription || description);
  const url = pageUrl(input.path);
  const image = absoluteOgImage(input.image || input.ogImage || input.coverImage);
  const usingDefault = image === `${SITE_ORIGIN}${DEFAULT_OG_IMAGE_PATH}`;
  const imageWidth = input.imageWidth || (usingDefault ? DEFAULT_OG_IMAGE_WIDTH : undefined);
  const imageHeight = input.imageHeight || (usingDefault ? DEFAULT_OG_IMAGE_HEIGHT : undefined);
  const type = oneLine(input.type || input.ogType || "website") || "website";
  const keywords = oneLine(input.keywords);
  const robots = oneLine(input.robots);
  const imageAlt = oneLine(input.imageAlt) || (usingDefault ? SITE_NAME : title);

  return {
    title,
    description,
    ogDescription,
    url,
    image,
    imageType: imageMime(image),
    imageWidth,
    imageHeight,
    imageAlt,
    type,
    keywords,
    robots,
    usingDefault,
  };
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function renderOpenGraphBlock(meta) {
  const tags = [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}" />`,
    `<link rel="canonical" href="${escapeHtml(meta.url)}" />`,
    `<meta property="og:title" content="${escapeHtml(meta.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(meta.ogDescription)}" />`,
    `<meta property="og:type" content="${escapeHtml(meta.type)}" />`,
    `<meta property="og:url" content="${escapeHtml(meta.url)}" />`,
    `<meta property="og:image" content="${escapeHtml(meta.image)}" />`,
    `<meta property="og:image:secure_url" content="${escapeHtml(meta.image)}" />`,
    `<meta property="og:image:type" content="${escapeHtml(meta.imageType)}" />`,
  ];
  if (meta.imageWidth && meta.imageHeight) {
    tags.push(`<meta property="og:image:width" content="${escapeHtml(meta.imageWidth)}" />`);
    tags.push(`<meta property="og:image:height" content="${escapeHtml(meta.imageHeight)}" />`);
  }
  tags.push(
    `<meta property="og:image:alt" content="${escapeHtml(meta.imageAlt)}" />`,
    `<meta property="og:site_name" content="${escapeHtml(SITE_NAME)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeHtml(meta.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(meta.ogDescription)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(meta.image)}" />`,
    `<meta name="twitter:image:alt" content="${escapeHtml(meta.imageAlt)}" />`,
  );
  if (meta.keywords) {
    tags.push(`<meta name="keywords" content="${escapeHtml(meta.keywords)}" />`);
  }
  if (meta.robots) {
    tags.push(`<meta name="robots" content="${escapeHtml(meta.robots)}" />`);
  }
  return tags.map((tag) => `        ${tag}`).join("\n");
}

// Meta sentinels, not HTML comments: CRA's production html minifier strips comments.
const BLOCK_RE = /<meta\s+[^>]*name="open-graph:start"[^>]*>[\s\S]*?<meta\s+[^>]*name="open-graph:end"[^>]*>/;

export function applyOpenGraphTemplate(html, meta) {
  if (!BLOCK_RE.test(html)) {
    throw new Error('HTML template is missing <meta name="open-graph:start"> markers');
  }
  const block = [
    '<meta name="open-graph:start" content="true" />',
    renderOpenGraphBlock(meta),
    '        <meta name="open-graph:end" content="true" />',
  ].join("\n");
  return html.replace(BLOCK_RE, block);
}
